(() => {
  const selector = [
    '.lt-card.leks-studio-vehicle .lt-image img',
    '.grid-product.leks-studio-vehicle .grid-product__image-mask img',
    '.product-section.leks-studio-vehicle .product-image-main .image-wrap img',
    '.product-section.leks-studio-vehicle .image-wrap__thumbnail img'
  ].join(',');
  const bound = new WeakSet();
  const processed = new WeakMap();
  const cache = new Map();
  const properties = ['width', 'height', 'left', 'top'];

  function measure(image) {
    const size = Math.min(256, Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * size / Math.max(image.naturalWidth, image.naturalHeight)));
    const height = Math.max(1, Math.round(image.naturalHeight * size / Math.max(image.naturalWidth, image.naturalHeight)));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', {willReadFrequently: true});
    if (!context) return null;
    context.drawImage(image, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height).data;
    const rows = new Uint16Array(height);
    const columns = new Uint16Array(width);
    let edges = 0;
    let lightEdges = 0;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const offset = (y * width + x) * 4;
        const light = pixels[offset + 3] < 16 ||
          (pixels[offset] >= 235 && pixels[offset + 1] >= 235 && pixels[offset + 2] >= 235);
        if (x === 0 || y === 0 || x === width - 1 || y === height - 1) {
          edges += 1;
          if (light) lightEdges += 1;
        }
        if (!light) {
          rows[y] += 1;
          columns[x] += 1;
        }
      }
    }
    if (lightEdges / edges < 0.9) return null;
    let left = 0;
    let right = width - 1;
    let top = 0;
    let bottom = height - 1;
    while (left < right && columns[left] < 2) left += 1;
    while (right > left && columns[right] < 2) right -= 1;
    while (top < bottom && rows[top] < 2) top += 1;
    while (bottom > top && rows[bottom] < 2) bottom -= 1;
    left = Math.max(0, left - 2);
    right = Math.min(width - 1, right + 2);
    top = Math.max(0, top - 2);
    bottom = Math.min(height - 1, bottom + 2);
    const boxWidth = right - left + 1;
    const boxHeight = bottom - top + 1;
    if (boxWidth < width * 0.15 || boxHeight < height * 0.15) return null;
    return {left, top, width: boxWidth, height: boxHeight, canvasWidth: width, canvasHeight: height};
  }

  async function getBounds(image, source) {
    if (cache.has(source)) return cache.get(source);
    let bounds;
    try {
      bounds = measure(image);
    } catch {
      const readable = new Image();
      readable.crossOrigin = 'anonymous';
      readable.src = source;
      await readable.decode();
      bounds = measure(readable);
    }
    cache.set(source, bounds);
    return bounds;
  }

  async function fit(image) {
    if (!image.complete || image.naturalWidth < 2 || image.naturalHeight < 2) return;
    const source = image.currentSrc || image.src;
    if (!source || source.includes('no-image') || processed.get(image) === source) return;
    processed.set(image, source);
    image.classList.remove('leks-motif-fit');
    for (const property of properties) image.style.removeProperty('--leks-motif-' + property);
    delete image.dataset.leksMotifBounds;
    try {
      const box = await getBounds(image, source);
      if ((image.currentSrc || image.src) !== source) {
        processed.delete(image);
        fit(image);
        return;
      }
      if (!box) return;
      const frameRatio = image.closest('.lt-card, .grid-product') ? 1 : 16 / 9;
      const motifRatio = box.width / box.height;
      const motifWidth = Math.min(0.98, 0.98 * motifRatio / frameRatio);
      const motifHeight = motifWidth * frameRatio / motifRatio;
      const values = {
        width: motifWidth * box.canvasWidth / box.width * 100,
        height: motifHeight * box.canvasHeight / box.height * 100,
        left: ((1 - motifWidth) / 2 - motifWidth * box.left / box.width) * 100,
        top: ((1 - motifHeight) / 2 - motifHeight * box.top / box.height) * 100
      };
      for (const property of properties) image.style.setProperty('--leks-motif-' + property, values[property].toFixed(5) + '%');
      image.dataset.leksMotifBounds = JSON.stringify(box);
      image.classList.add('leks-motif-fit');
    } catch {
      image.classList.remove('leks-motif-fit');
    }
  }

  function scan(root) {
    const images = root.matches?.(selector) ? [root] : [];
    if (root.querySelectorAll) images.push(...root.querySelectorAll(selector));
    for (const image of images) {
      if (!bound.has(image)) {
        bound.add(image);
        image.addEventListener('load', () => fit(image));
      }
      fit(image);
    }
  }

  function start() {
    scan(document);
    document.addEventListener('shopify:section:load', event => scan(event.target));
    new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'attributes') scan(record.target);
        else for (const node of record.addedNodes) if (node.nodeType === 1) scan(node);
      }
    }).observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset']});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
